import React, { useState, useRef, useCallback, useEffect, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Zap, CheckCircle2,
  RefreshCcw, Wallet, Eye, Upload, Ban, Info, AlertCircle, Loader2,
  Shield, ScanLine, BarChart3, FileText, HelpCircle, X, BellRing, Clock, Download,
} from "lucide-react";
import HowToDoSection from "../landing/components/HowToDoSection";
import PolicyResultModal from "./components/PolicyResultModal";
import { HistoryResultsModal } from "./HistoryPage";
import UpgradeLimitModal from "../../components/ui/UpgradeLimitModal";
import Modal from "../../components/ui/Modal";
import TrialPaymentStep from "../auth/components/TrialPaymentStep";
import { useAuth } from "../auth/hooks/useAuth";
import { adminService } from "../admin/services/adminService";
import { getLastAnalysis, getRevisionReport, getAssetReport, getPolicyDocuments, downloadPolicyDocument } from "./services/dashboardService";
import { useAnalysis } from "./store/analysisContext";

// ── Transform API report_json → result shape expected by PolicyResultModal ────
const transformResult = (reportJson, context = {}) => {
  if (!reportJson) return null;

  const privacyAnalysis = reportJson.privacy_analysis || {};
  const categories = Object.entries(privacyAnalysis).map(([name, data]) => ({
    name,
    score:       data.score           ?? 0,
    maxScore:    10,
    confidence:  data.confidence_score ?? 0,
    verdict:     data.indicator        ?? "Unknown",
    description: data.summary          ?? "",
  }));

  const overallScore =
    categories.length > 0
      ? parseFloat(
          (categories.reduce((s, c) => s + c.score, 0) / categories.length).toFixed(1)
        )
      : 0;

  const avgConfidence =
    categories.length > 0
      ? Math.round(categories.reduce((s, c) => s + c.confidence, 0) / categories.length)
      : 0;

  const meta = reportJson.report_metadata || {};

  return {
    // title:              meta.report_title ?? "Privacy Analysis",
    reviewedAt:         meta.review_date
                          ? new Date(meta.review_date).toLocaleDateString("en-US", {
                              month: "short", day: "numeric", year: "numeric",
                            })
                          : new Date().toLocaleDateString(),
    policyAsOf:         reportJson.policy_date ?? null,
    overallScore,
    categoriesAnalyzed: categories.length,
    avgConfidence,
    toolSummary:        reportJson.tool_summary ?? reportJson.website_summary ?? "",
    executiveSummary:   reportJson.executive_summary ?? "",
    summary:            reportJson.summary           ?? "",
    softwareName:       context.software_name ?? context.asset?.software_name ?? "",
    sourceUrl:          context.privacy_url ?? meta.source_privacy_policy_url ?? "",
    // Needed to fetch the archived source PDF. The three analysis paths return
    // the asset id in different shapes: run-once and reanalyse nest it under
    // `revision`, a cache hit returns it flat as `id`.
    assetId:            context.revision?.asset_id ?? context.asset?.id
                          ?? context.asset_id ?? context.id ?? null,
    categories,
  };
};

// ── helpers ───────────────────────────────────────────────────────────────────
// eslint-disable-next-line no-unused-vars
const scoreColor = (score) => {
  if (score >= 7) return "text-success";
  if (score >= 4) return "text-warning";
  return "text-danger";
};

// eslint-disable-next-line no-unused-vars
const scoreLabel = (score) => {
  if (score >= 7) return "Good";
  if (score >= 4) return "Fair";
  return "Poor";
};

// eslint-disable-next-line no-unused-vars
const badgeClass = (verdict) => {
  const v = (verdict || "").toLowerCase();
  if (v === "good" || v === "strong") return "bg-success/10 text-success";
  if (v === "fair" || v === "moderate") return "bg-warning/10 text-warning";
  return "bg-danger/10 text-danger";
};

// ── Analysis steps ────────────────────────────────────────────────────────────
const STEPS = [
  { label: "Processing Document",   icon: FileText,   desc: "Extracting policy content & structure"   },
  { label: "Scanning Clauses",       icon: ScanLine,   desc: "Identifying data clauses & obligations"    },
  { label: "Analyzing Compliance",   icon: Shield,     desc: "Checking GDPR, CCPA & regulatory gaps"   },
  { label: "Generating Report",      icon: BarChart3,  desc: "Compiling risk scores & recommendations"  },
];

// ── Build result from GET /api/v1/history/asset/{id}/report response ─────────
// Preferred display order + friendly labels for known categories. This is a
// hint, NOT a whitelist: every category the server returns is rendered. Known
// keys get their label and fixed position; any unknown/new key (e.g. a future
// category) is appended in the order the server provided it.
const HIST_CATEGORY_KEYS = [
  { key: "Data Usage",                  label: "Data Usage"                  },
  { key: "Data Sharing",                label: "Data Sharing"                },
  { key: "Security Commitments",        label: "Security Commitments"        },
  { key: "Confidentiality Protections", label: "Confidentiality Protections" },
  { key: "Data Retention and Deletion", label: "Data Retention & Deletion"   },
  { key: "AI Training & Model Rights",  label: "AI Training & Model Rights"  },
];
const HIST_LABELS = HIST_CATEGORY_KEYS.reduce((m, { key, label }) => {
  m[key] = label;
  return m;
}, {});
const HIST_ORDER = HIST_CATEGORY_KEYS.map((c) => c.key);

const buildResultFromReport = (data) => {
  const asset   = data?.asset  ?? {};
  const report  = data?.report ?? {};
  const rj      = report?.report_json ?? {};
  const privacy = rj?.privacy_analysis ?? {};

  // Render every category present in the server response (dynamic, like
  // transformResult). Known keys sort to their preferred position; unknown
  // keys keep server order at the end (Array.sort is stable).
  const entries = Object.entries(privacy).sort(([a], [b]) => {
    const ia = HIST_ORDER.indexOf(a);
    const ib = HIST_ORDER.indexOf(b);
    if (ia === -1 && ib === -1) return 0;
    if (ia === -1) return 1;
    if (ib === -1) return -1;
    return ia - ib;
  });
  const categories = entries.map(([key, val]) => ({
    name:        HIST_LABELS[key] ?? key,
    score:       val?.score            ?? 0,
    maxScore:    10,
    confidence:  val?.confidence_score ?? 0,
    verdict:     val?.indicator        ?? "Neutral",
    description: val?.summary          ?? "",
  }));

  // Average across ALL returned categories (do not filter out 0 scores —
  // a genuine 0 is a real data point and must count toward the average).
  const overallScore = categories.length
    ? +(categories.reduce((s, c) => s + c.score, 0) / categories.length).toFixed(1)
    : 0;
  const avgConfidence = categories.length
    ? Math.round(categories.reduce((s, c) => s + c.confidence, 0) / categories.length)
    : null;
  return {
    title:              rj?.report_metadata?.report_title ?? "Privacy and Data Handling Review",
    reviewedAt:         rj?.report_metadata?.review_date ?? report?.created_at ?? "",
    policyAsOf:         report?.policy_date_raw ?? rj?.policy_date ?? null,
    overallScore,
    categoriesAnalyzed: categories.length,
    avgConfidence,
    toolSummary:        rj?.website_summary   ?? "",
    executiveSummary:   rj?.executive_summary ?? "",
    categories,
    softwareName:       asset?.software_name  ?? "",
  };
};

// ── Framer Motion variants ─────────────────────────────────────────────────────
const fadeUpVariants = {
  hidden: { opacity: 0, y: 18 },
  show:   { opacity: 1, y: 0, transition: { duration: 0.45, ease: "easeOut" } },
};

const pillVariants = {
  hidden: { opacity: 0, scale: 0.7 },
  show:   { opacity: 1, scale: 1, transition: { type: "spring", stiffness: 380, damping: 18 } },
};

// ── Sub-components ────────────────────────────────────────────────────────────
// Visible foreground countdown. Must match HANDOFF_MS in analysisContext: when
// it reaches 0 the job has been handed off to the background and this view is
// replaced by the handoff panel.
const COUNTDOWN_SECS = 45;

const AnalyzingView = ({ step, softwareName, onNewAnalysis, onCancelAnalysis, startTime }) => {
  const clampedStep = Math.min(step, STEPS.length);
  const activeIndex = step < STEPS.length ? step : STEPS.length - 1;
  const allDone     = step >= STEPS.length;

  // Randomised milestones — never land on neat 25/50/75/100.
  const milestones = useMemo(() => [
    23 + Math.floor(Math.random() * 7),   // step 1 → 23–29 %
    47 + Math.floor(Math.random() * 7),   // step 2 → 47–53 %
    71 + Math.floor(Math.random() * 8),   // step 3 → 71–78 %
    100,
  ], [startTime]); // eslint-disable-line

  // The target % for the current step.
  const targetProgress = allDone
    ? 100
    : step > 0 ? milestones[Math.min(step - 1, milestones.length - 1)] : 0;

  // displayProgress: what the bar + number actually show.
  // Counts up rapidly to each milestone, then drifts slowly while
  // "Generating Report" waits for the API response.
  const [displayProgress, setDisplayProgress] = useState(0);
  const dpRef = useRef(0);

  // 1. Count-up toward milestone: fast when far away, decelerates near target.
  //    55ms/tick — slow enough to visibly read, ~2s for a ~25-step jump.
  useEffect(() => {
    if (dpRef.current === targetProgress) return;
    const id = setInterval(() => {
      const gap = targetProgress - dpRef.current;
      const step = gap > 8 ? 2 : 1;          // decelerate in the last 8%
      dpRef.current = Math.min(dpRef.current + step, targetProgress);
      setDisplayProgress(dpRef.current);
      if (dpRef.current >= targetProgress) clearInterval(id);
    }, 55);
    return () => clearInterval(id);
  }, [targetProgress]); // eslint-disable-line

  // 2. Slow drift (+1% every 2.5s) while waiting for API on last step.
  const isLastStep = !allDone && step === STEPS.length - 1;
  useEffect(() => {
    if (!isLastStep) return;
    const init = setTimeout(() => {
      const id = setInterval(() => {
        setDisplayProgress((prev) => {
          const next = Math.min(prev + 1, 95);
          dpRef.current = next;
          return next;
        });
      }, 2500);
      return () => clearInterval(id);
    }, 900);
    return () => clearTimeout(init);
  }, [isLastStep]); // eslint-disable-line

  // 3. Snap to 100% immediately when done.
  useEffect(() => {
    if (!allDone) return;
    dpRef.current = 100;
    setDisplayProgress(100);
  }, [allDone]);

  // ── Countdown timer ────────────────────────────────────────────────────────
  const [remaining, setRemaining] = useState(() => {
    if (!startTime) return COUNTDOWN_SECS;
    const elapsed = Math.floor((Date.now() - startTime) / 1000);
    return Math.max(COUNTDOWN_SECS - elapsed, 0);
  });

  useEffect(() => {
    if (allDone || remaining <= 0) return;
    const id = setInterval(() => {
      setRemaining((r) => Math.max(r - 1, 0));
    }, 1000);
    return () => clearInterval(id);
  }, [allDone]); // eslint-disable-line react-hooks/exhaustive-deps

  const mins = String(Math.floor(remaining / 60));
  const secs = String(remaining % 60).padStart(2, "0");

  return (
    <motion.div
      className="relative overflow-hidden px-6 py-10"
      initial="hidden"
      animate="show"
      variants={fadeUpVariants}
    >
      {/* Soft radial glow */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "radial-gradient(ellipse 65% 45% at 50% 0%, rgba(99,102,241,0.07) 0%, transparent 68%)",
        }}
      />

      <div className="relative flex flex-col items-center gap-7">

        {/* ── Orbital scanner orb ── */}
        <motion.div
          className="relative w-[7.5rem] h-[7.5rem] flex items-center justify-center select-none"
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5, ease: "easeOut" }}
        >
          {/* Outer slow ping */}
          <span
            className="absolute inset-0 rounded-full border border-primary/10 animate-ping"
            style={{ animationDuration: "2.8s" }}
          />
          {/* Dashed slow-spin */}
          <motion.span
            className="absolute inset-3 rounded-full border border-dashed border-primary/20"
            animate={{ rotate: 360 }}
            transition={{ duration: 9, repeat: Infinity, ease: "linear" }}
          />
          {/* Fast counter-spin */}
          <motion.span
            className="absolute inset-[1.4rem] rounded-full border border-primary/35"
            animate={{ rotate: -360 }}
            transition={{ duration: 3.8, repeat: Infinity, ease: "linear" }}
          />

          {/* Center disc */}
          <motion.div
            className="relative z-10 w-[3.25rem] h-[3.25rem] rounded-full overflow-hidden
              bg-gradient-to-br from-primary/20 to-primary/5 border border-primary/30
              flex items-center justify-center"
            animate={!allDone ? {
              boxShadow: [
                "0 0 0px 0px rgba(99,102,241,0)",
                "0 0 22px 5px rgba(99,102,241,0.18)",
                "0 0 0px 0px rgba(99,102,241,0)",
              ],
            } : { boxShadow: "0 0 0px 0px rgba(99,102,241,0)" }}
            transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
          >
            {/* Vertical scan line — only while running */}
            <AnimatePresence>
              {!allDone && (
                <motion.span
                  key="scanline"
                  className="absolute left-0 right-0 h-[2px] bg-primary/40 rounded-full pointer-events-none"
                  initial={{ top: "10%", opacity: 0 }}
                  animate={{ top: ["10%", "85%", "10%"], opacity: [0.7, 0.4, 0.7] }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                />
              )}
            </AnimatePresence>

            <AnimatePresence mode="wait">
              {allDone ? (
                <motion.div
                  key="done"
                  initial={{ scale: 0, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ type: "spring", stiffness: 400, damping: 15 }}
                >
                  <CheckCircle2 className="w-6 h-6 text-success" />
                </motion.div>
              ) : (
                <motion.div
                  key="loading"
                  initial={{ scale: 0, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 0, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                >
                  <Loader2 className="w-6 h-6 text-primary animate-spin" />
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        </motion.div>

        {/* ── Headline ── */}
        <motion.div
          className="text-center"
          variants={fadeUpVariants}
          initial="hidden"
          animate="show"
          transition={{ delay: 0.1 }}
        >
          <AnimatePresence mode="wait">
            <motion.h2
              key={allDone ? "done" : "running"}
              className="text-2xl font-bold text-brand-text tracking-tight"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.3 }}
            >
              {allDone ? "Analysis Complete" : "Analyzing Policy…"}
            </motion.h2>
          </AnimatePresence>
          <p className="text-sm text-brand-muted mt-1">
            Our AI is reviewing every clause for risks and compliance.
          </p>
          <p className="text-sm mt-1 text-brand-muted">
            <span className="text-primary font-semibold">{softwareName || "Your policy"}</span>
            {!allDone && (
              <>
                {" — "}
                <span className="text-primary font-semibold tabular-nums">{mins}:{secs}</span>
                {" "}
                <span className="text-brand-muted">
                  {remaining > 0 ? "remaining" : "almost done…"}
                </span>
              </>
            )}
          </p>
        </motion.div>

        {/* ── Progress bar ── */}
        <motion.div
          className="w-full max-w-sm"
          variants={fadeUpVariants}
          initial="hidden"
          animate="show"
          transition={{ delay: 0.2 }}
        >
          <div className="flex justify-between items-center text-xs mb-1.5">
            <span className="text-brand-muted font-medium">Overall progress</span>
            <motion.span
              className="text-primary font-bold tabular-nums"
              key={step}
              initial={{ opacity: 0, y: -4 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.25 }}
            >
              {displayProgress}%
            </motion.span>
          </div>
          <div className="relative h-2 w-full bg-surface-border rounded-full overflow-hidden">
            <motion.div
              className="h-full bg-gradient-to-r from-primary to-indigo-400 rounded-full"
              initial={{ width: "0%" }}
              animate={{ width: `${displayProgress}%` }}
              transition={{ duration: 0.7, ease: "easeOut" }}
            />
            {/* Shimmer sweep */}
            <motion.span
              aria-hidden
              className="absolute inset-y-0 w-1/3"
              style={{
                background:
                  "linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.5) 50%, transparent 100%)",
              }}
              animate={{ x: ["-100%", "350%"] }}
              transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut", repeatDelay: 0.4 }}
            />
          </div>
        </motion.div>

        {/* ── Step chips for completed steps ── */}
        <AnimatePresence>
          {(step > 0 || allDone) && (
            <motion.div
              className="w-full max-w-sm flex flex-wrap gap-1.5 justify-center"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              transition={{ duration: 0.35, ease: "easeOut" }}
            >
              {STEPS.slice(0, allDone ? STEPS.length : step).map((s, i) => (
                <motion.span
                  key={i}
                  initial={{ opacity: 0, scale: 0.75 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ type: "spring", stiffness: 380, damping: 18, delay: i * 0.06 }}
                  className="inline-flex items-center gap-1.5 text-xs font-semibold text-success bg-success/10 border border-success/20 px-3 py-1 rounded-full"
                >
                  <CheckCircle2 className="w-3 h-3" />
                  {s.label}
                </motion.span>
              ))}
            </motion.div>
          )}
        </AnimatePresence>

        {/* ── Sequential step cards: active (full) + next (dim preview) ── */}
        {!allDone && (
          <div className="w-full max-w-sm flex flex-col gap-2.5">
            <AnimatePresence mode="popLayout">
              {STEPS.map((s, i) => {
                const isActive = i === activeIndex;
                const isNext   = i === activeIndex + 1;
                if (!isActive && !isNext) return null;

                const StepIcon = s.icon;

                return (
                  <motion.div
                    key={i}
                    layout
                    initial={{ opacity: 0, y: 28, scale: 0.96 }}
                    animate={
                      isActive
                        ? { opacity: 1,    y: 0,  scale: 1,    filter: "blur(0px)" }
                        : { opacity: 0.38, y: 10, scale: 0.97, filter: "blur(0.6px)" }
                    }
                    exit={{ opacity: 0, y: -28, scale: 0.95 }}
                    transition={{ duration: 0.42, ease: [0.25, 0.46, 0.45, 0.94] }}
                    className={`relative flex items-center gap-3 px-4 py-3.5 rounded-xl border overflow-hidden ${
                      isActive
                        ? "border-primary/30 bg-primary/5 shadow-sm"
                        : "border-surface-border/50 bg-transparent"
                    }`}
                  >
                    {/* Shimmer sweep only on active */}
                    {isActive && (
                      <motion.span
                        aria-hidden
                        className="pointer-events-none absolute inset-y-0 w-2/5"
                        style={{
                          background:
                            "linear-gradient(90deg, transparent 0%, rgba(99,102,241,0.09) 50%, transparent 100%)",
                        }}
                        initial={{ x: "-100%" }}
                        animate={{ x: "280%" }}
                        transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut", repeatDelay: 0.5 }}
                      />
                    )}

                    {/* Icon */}
                    <div
                      className={`relative z-10 w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${
                        isActive ? "bg-primary/10" : "bg-surface-border/40"
                      }`}
                    >
                      {isActive ? (
                        <Loader2 className="w-4 h-4 text-primary animate-spin" />
                      ) : (
                        <StepIcon className="w-4 h-4 text-brand-light" />
                      )}
                    </div>

                    {/* Text */}
                    <div className="relative z-10 flex-1 min-w-0">
                      <p className={`text-sm font-semibold leading-tight ${isActive ? "text-brand-text" : "text-brand-muted"}`}>
                        {s.label}
                      </p>
                      <p className={`text-xs mt-0.5 truncate ${isActive ? "text-primary/70" : "text-brand-light"}`}>
                        {s.desc}
                      </p>
                    </div>

                    {/* Status pill */}
                    <div className="relative z-10 flex-shrink-0">
                      {isActive ? (
                        <motion.span
                          variants={pillVariants}
                          initial="hidden"
                          animate="show"
                          className="inline-flex text-xs font-semibold text-primary bg-primary/10 px-2.5 py-0.5 rounded-full"
                        >
                          <motion.span
                            animate={{ opacity: [1, 0.4, 1] }}
                            transition={{ duration: 1.2, repeat: Infinity, ease: "easeInOut" }}
                          >
                            Running
                          </motion.span>
                        </motion.span>
                      ) : (
                        <span className="inline-flex text-xs font-medium text-brand-light bg-surface-border/60 px-2.5 py-0.5 rounded-full">
                          Next
                        </span>
                      )}
                    </div>
                  </motion.div>
                );
              })}
            </AnimatePresence>
          </div>
        )}

        {/* ── Note ── */}
        {/* <motion.p
          className="text-xs text-center text-brand-light max-w-sm leading-relaxed"
          variants={fadeUpVariants}
          initial="hidden"
          animate="show"
          transition={{ delay: 0.55 }}
        >
          <span className="font-semibold text-amber-500">Note:</span>{" "}
          You can start a new analysis — this one keeps running in the background.
        </motion.p> */}

        {/* ── Actions ── */}
        <motion.div
          className="flex items-center gap-3 flex-wrap justify-center"
          variants={fadeUpVariants}
          initial="hidden"
          animate="show"
          transition={{ delay: 0.65 }}
        >
          <motion.button
            onClick={onCancelAnalysis}
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.97 }}
            className="inline-flex items-center gap-2 text-danger border border-danger/30 bg-danger/5 px-5 py-2.5 rounded-xl text-sm font-semibold hover:bg-danger/10 transition-colors"
          >
            <Ban className="w-4 h-4" />
            Cancel Analysis
          </motion.button>
          {/* <motion.button
            onClick={onNewAnalysis}
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.97 }}
            className="inline-flex items-center gap-2 bg-primary text-white px-5 py-2.5 rounded-xl text-sm font-semibold hover:bg-primary-dark transition-colors shadow-sm"
          >
            <RefreshCcw className="w-4 h-4" />
            New Analysis
          </motion.button> */}
        </motion.div>

      </div>
    </motion.div>
  );
};

// ── "Asset Limit Reached" modal ─────────────────────────────────────────────
const AssetLimitModal = ({ message, onClose, onUpgrade }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
    <div className="w-full max-w-sm rounded-2xl overflow-hidden shadow-2xl">
      <div className="bg-gradient-to-b from-orange-500 to-orange-600 px-6 pt-6 pb-5 text-white">
        <div className="w-9 h-9 rounded-full bg-white/20 flex items-center justify-center mb-4">
          <Wallet className="w-5 h-5 text-white" />
        </div>
        <h3 className="text-lg font-bold mb-1">Asset Limit Reached</h3>
        <p className="text-sm text-white/80 leading-relaxed">
          {message || "You have reached the maximum number of assets allowed on your current plan."}
        </p>
        <div className="flex items-center gap-3 mt-5">
          <button
            onClick={onClose}
            className="flex-1 py-2.5 rounded-full border border-white text-white text-sm font-semibold hover:bg-white/10 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={onUpgrade}
            className="flex-1 py-2.5 rounded-full bg-white text-orange-600 text-sm font-semibold hover:bg-white/90 transition-colors"
          >
            Upgrade Plan
          </button>
        </div>
      </div>
      <div className="bg-white px-6 py-3 text-center">
        <p className="text-xs text-brand-muted">
          {new Date().toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
        </p>
      </div>
    </div>
  </div>
);

// ── "Already Analyzed" confirmation modal ────────────────────────────────────
const AlreadyAnalyzedModal = ({ assetId, uploadedFile, onCancel, onView, onReanalyse, onReanalyseWithFile, busy = false }) => {
  const [docs, setDocs] = useState([]);
  const [docsLoading, setDocsLoading] = useState(false);

  useEffect(() => {
    if (!assetId) return undefined;
    let alive = true;
    setDocsLoading(true);
    getPolicyDocuments(assetId)
      .then((res) => { if (alive) setDocs(res?.data?.documents ?? []); })
      .catch(() => { if (alive) setDocs([]); })
      .finally(() => { if (alive) setDocsLoading(false); });
    return () => { alive = false; };
  }, [assetId]);

  const fmtDate = (d) =>
    d ? new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="relative w-full max-w-md rounded-2xl overflow-hidden shadow-2xl bg-white">
        {/* X (Cancel) */}
        <button
          onClick={onCancel}
          aria-label="Cancel"
          className="absolute top-3 right-3 z-10 w-8 h-8 flex items-center justify-center rounded-full text-white/90 hover:bg-white/20 transition-colors"
        >
          <X className="w-4 h-4" />
        </button>

        {/* gradient header */}
        <div className="bg-gradient-to-b from-[#e8553a] to-[#d94425] px-6 pt-6 pb-5 text-white">
          <div className="w-9 h-9 rounded-full bg-white/20 flex items-center justify-center mb-3">
            <Info className="w-5 h-5 text-white" />
          </div>
          <h3 className="text-lg font-bold mb-1">Existing report found</h3>
          <p className="text-sm text-white/85 leading-relaxed">
            This privacy policy URL has already been analysed.{" "}
            {uploadedFile
              ? "Upload your file as a new policy version, or view the existing report."
              : "Choose how you'd like to proceed."}
          </p>
        </div>

        {/* existing document versions on file */}
        <div className="px-6 pt-4">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-brand-muted mb-2">
            Policy documents on file
          </p>
          {docsLoading ? (
            <div className="flex items-center gap-2 text-xs text-brand-muted py-2">
              <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading versions…
            </div>
          ) : docs.length === 0 ? (
            <div className="text-xs text-brand-muted py-2">No documents stored for this vendor yet.</div>
          ) : (
            <ul className="space-y-1.5 max-h-40 overflow-auto">
              {docs.map((d) => (
                <li
                  key={d.id}
                  className="flex items-center justify-between gap-2 rounded-lg border border-surface-border px-3 py-2"
                >
                  <span className="min-w-0">
                    <span className="block text-xs font-medium text-brand-text truncate">{d.file_name}</span>
                    <span className="block text-[11px] text-brand-muted">V{d.version} · {fmtDate(d.created_at)}</span>
                  </span>
                  <button
                    onClick={() => downloadPolicyDocument(assetId, d.id, d.file_name)}
                    className="shrink-0 inline-flex items-center gap-1 text-[11px] font-medium text-primary hover:underline"
                    title="Download this version"
                  >
                    <Download className="w-3.5 h-3.5" /> Download
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* options */}
        <div className="px-6 py-5 space-y-3">
          <button
            onClick={onView}
            disabled={busy}
            className="w-full flex flex-col items-start gap-1 rounded-xl border border-primary/30 bg-primary/5 px-4 py-3 text-left hover:bg-primary/10 transition-colors disabled:opacity-60"
          >
            <span className="flex items-center gap-2 text-sm font-semibold text-primary">
              <Eye className="w-4 h-4" /> View existing report
            </span>
            <span className="text-xs text-brand-muted leading-snug">
              Fetch the latest existing report instantly — no credits will be used.
            </span>
          </button>

          {uploadedFile ? (
            <button
              onClick={onReanalyseWithFile}
              disabled={busy}
              className="w-full flex flex-col items-start gap-1 rounded-xl border border-surface-border px-4 py-3 text-left hover:bg-surface/60 transition-colors disabled:opacity-60"
            >
              <span className="flex items-center gap-2 text-sm font-semibold text-brand-text">
                <Upload className="w-4 h-4" /> Upload new version &amp; re-analyse
              </span>
              <span className="text-xs text-brand-muted leading-snug">
                Store “{uploadedFile.name}” as a new version and run a fresh analysis — one credit will be used.
              </span>
            </button>
          ) : (
            <button
              onClick={onReanalyse}
              disabled={busy}
              className="w-full flex flex-col items-start gap-1 rounded-xl border border-surface-border px-4 py-3 text-left hover:bg-surface/60 transition-colors disabled:opacity-60"
            >
              <span className="flex items-center gap-2 text-sm font-semibold text-brand-text">
                <RefreshCcw className="w-4 h-4" /> Re-analyse
              </span>
              <span className="text-xs text-brand-muted leading-snug">
                Start a new analysis against the last stored document — one credit will be used.
              </span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// ── Main page ─────────────────────────────────────────────────────────────────
const AnalyzePage = () => {
  const navigate = useNavigate();
  const { state: ctxState, runAnalysis, runReanalysis, cancelAnalysis, resetAnalysis, setActiveId } = useAnalysis();
  const { analyses, activeId } = ctxState;

  // ── Derive UI flags from currently-active analysis ──────────────────────────
  const activeAnalysis    = analyses[activeId] ?? null;
  const view              = activeAnalysis?.status === "running"    ? "analyzing"
                          : activeAnalysis?.status === "background" ? "handoff"
                          : "form";
  const step              = activeAnalysis?.step ?? 0;
  const result            = activeAnalysis?.result ?? null;
  const submitting        = activeAnalysis?.status === "running";
  const showModal         = activeAnalysis?.status === "done";
  const backgroundMsg     = activeAnalysis?.status === "background" ? activeAnalysis.backgroundMsg : "";
  const analyzeError      = (activeAnalysis && !activeAnalysis.upgradePrompt && !activeAnalysis.alreadyAnalyzed)
                              ? activeAnalysis.errorMsg
                              : "";
  const analyzeErrorTitle = activeAnalysis?.errorTitle || "Analysis Failed";
  const showAlreadyAnalyzed = !!activeAnalysis?.alreadyAnalyzed;
  const upgradeModal      = activeAnalysis?.upgradePrompt ? activeAnalysis.errorMsg : null;
  // Trial spent (allowance used or day 30 passed): this is the moment we ask for
  // a card, rather than pushing a plan upgrade.
  const trialEndedMsg     = activeAnalysis?.trialEnded ? activeAnalysis.errorMsg : null;

  // ── Local-only state (does not need to survive navigation) ─────────────────────
  const [form, setForm]               = useState({ softwareName: "", policyUrl: "", notes: "" });
  const [uploadedFile, setUploadedFile] = useState(null);
  const [urlError, setUrlError]       = useState("");
  const fileInputRef = useRef(null);

  // ── Latest analysis state ──────────────────────────────────────────────────
  const [lastAnalysis,      setLastAnalysis]      = useState(null);
  const [lastLoading,       setLastLoading]       = useState(true);
  const [showViewModal,     setShowViewModal]     = useState(false);
  const [viewRow,           setViewRow]           = useState(null);
  const [errorModalOpen,    setErrorModalOpen]    = useState(false);
  const [trialPlan,         setTrialPlan]         = useState(null);
  const { state: authState } = useAuth();

  // Fetched only when the trial actually ends, so the normal path costs nothing.
  useEffect(() => {
    if (!trialEndedMsg || trialPlan) return;
    adminService.getPlans()
      .then((res) => {
        const raw = Array.isArray(res) ? res
                  : Array.isArray(res?.data?.plans) ? res.data.plans
                  : Array.isArray(res?.data) ? res.data
                  : [];
        const found = raw.find((p) => p.name?.toLowerCase() === 'individual');
        if (found) setTrialPlan({ id: found.id, name: found.name, price: Number(found.price) || 0 });
      })
      .catch(() => {});
  }, [trialEndedMsg, trialPlan]);

  // Auto-open error modal when analyzeError appears
  const prevAnalyzeError = React.useRef("");
  useEffect(() => {
    if (analyzeError && analyzeError !== prevAnalyzeError.current) {
      setErrorModalOpen(true);
    }
    prevAnalyzeError.current = analyzeError;
  }, [analyzeError]);

  // Dismiss the error modal AND clear the error from global state. Closing only
  // the local flag leaves the errored analysis in context, so the auto-open
  // effect re-fires (and re-pops the modal) whenever AnalyzePage remounts — e.g.
  // after navigating to History/Dashboard and back.
  const dismissError = useCallback(() => {
    setErrorModalOpen(false);
    if (activeId) resetAnalysis(activeId);
  }, [activeId, resetAnalysis]);

  // Safety net for the other exit path: if the user leaves the page while an
  // error is still showing (without clicking OK), clear it on unmount so it
  // doesn't re-pop on return. Guarded to errored analyses only — never touches a
  // running/background/done one. Refs avoid a stale closure in the cleanup.
  const errorCleanupRef = React.useRef({ id: null, hasError: false });
  useEffect(() => {
    errorCleanupRef.current = { id: activeId, hasError: !!analyzeError };
  }, [activeId, analyzeError]);
  useEffect(() => () => {
    const { id, hasError } = errorCleanupRef.current;
    if (id && hasError) resetAnalysis(id);
  }, [resetAnalysis]);

  // Re-analyse requested from History → Failed. Clear the router state before
  // starting so a refresh or back-navigation doesn't kick off another run.
  const location = useLocation();
  const reanalyseRequest = location.state?.reanalyse;
  useEffect(() => {
    if (!reanalyseRequest?.assetId) return;
    navigate(location.pathname, { replace: true, state: null });
    runReanalysis({
      existingAssetId: reanalyseRequest.assetId,
      softwareName:    reanalyseRequest.softwareName ?? "",
      transformFn:     transformResult,
    });
  }, [reanalyseRequest]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    getLastAnalysis()
      .then((res) => setLastAnalysis(res?.data?.analysis ?? null))
      .catch(() => setLastAnalysis(null))
      .finally(() => setLastLoading(false));
  }, []);

  // Refresh Latest Analysis table whenever an analysis completes
  useEffect(() => {
    if (!showModal) return;
    getLastAnalysis()
      .then((res) => setLastAnalysis(res?.data?.analysis ?? null))
      .catch(() => {});
  }, [showModal]);

  const handleViewLatest = useCallback(() => {
    if (!lastAnalysis) return;
    const revisionId = lastAnalysis?.revision_id ?? lastAnalysis?.revision?.id;
    const versionNum = lastAnalysis?.revision?.version_number ?? lastAnalysis?.version_number ?? 1;
    const dateStr = lastAnalysis?.created_at
      ? new Date(lastAnalysis.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
      : "";
    const row = {
      company:  lastAnalysis.software_name ?? "",
      assetId:  lastAnalysis.asset_id ?? lastAnalysis.asset?.id ?? null,
      privacyUrl: lastAnalysis.privacy_url ?? "",
      revisionId,
      date:     dateStr,
      versions: revisionId ? [{
        key:        `v${versionNum}`,
        label:      `V${versionNum}`,
        date:       dateStr,
        revisionId,
      }] : [],
    };
    setViewRow(row);
    setShowViewModal(true);
  }, [lastAnalysis]);

  const totalCredits     = 50;
  const usedCredits      = 0;
  const remainingCredits = Math.max(totalCredits - usedCredits, 0);
  const isCreditsExhausted = remainingCredits === 0;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
    if (name === "policyUrl") setUrlError("");
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const allowed = [".pdf", ".txt", ".json", ".md"];
    const ext = "." + file.name.split(".").pop().toLowerCase();
    if (!allowed.includes(ext)) {
      alert(`Unsupported file type. Please upload one of: PDF, TXT, JSON, MD`);
      e.target.value = "";
      return;
    }
    setUploadedFile(file);
  };

  const handleRemoveFile = () => {
    setUploadedFile(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.softwareName.trim() || !form.policyUrl.trim() || isCreditsExhausted) return;

    // Normalise scheme first so validation runs on the final URL.
    let candidate = form.policyUrl.trim();
    if (!/^https?:\/\//i.test(candidate)) candidate = `https://${candidate}`;

    // Reject query strings (tracking/session params). Fragments (#section) are OK.
    if (/\?/.test(candidate)) {
      setUrlError('Enter the plain policy URL — remove the query string (anything after "?").');
      return;
    }
    setUrlError("");

    if (candidate !== form.policyUrl.trim()) {
      setForm((prev) => ({ ...prev, policyUrl: candidate }));
      // Re-trigger after state update
      setTimeout(() => startAnalysis(), 0);
      return;
    }

    startAnalysis();
  };

  // ── Analysis flow — delegated to global AnalysisContext ──────────────────
  const startAnalysis = useCallback(() => {
    const fd = new FormData();
    fd.append("software_name", form.softwareName.trim());
    if (form.policyUrl.trim()) fd.append("privacy_url", form.policyUrl.trim());
    if (form.notes.trim())     fd.append("notes",       form.notes.trim());
    if (uploadedFile)          fd.append("policyFile",  uploadedFile);

    runAnalysis({
      formData:     fd,
      softwareName: form.softwareName.trim(),
      transformFn:  transformResult,
    });
    // Context dispatches ADD → sets activeId → view switches to "analyzing" automatically
  }, [form, uploadedFile, runAnalysis]); // eslint-disable-line

  // "View" from the already-analysed modal — fetch the existing report for the
  // duplicate asset and open it in the report viewer. No credit consumed.
  const handleViewExisting = useCallback(async () => {
    const assetId      = activeAnalysis?.existingAssetId;
    const softwareName = activeAnalysis?.softwareName ?? "";
    resetAnalysis(activeId); // dismiss the already-analysed entry
    if (!assetId) return;
    try {
      const res    = await getAssetReport(assetId);
      const data   = res?.data ?? {};
      const asset  = data.asset  ?? {};
      const report = data.report ?? {};
      const dateStr = report.created_at
        ? new Date(report.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
        : "";
      setViewRow({
        company:    asset.software_name ?? softwareName,
        assetId:    asset.id ?? assetId,
        privacyUrl: asset.privacy_url ?? "",
        revisionId: report.id ?? null,
        date:       dateStr,
        versions:   report.id ? [{
          key:        `v${report.version ?? 1}`,
          label:      `V${report.version ?? 1}`,
          date:       dateStr,
          revisionId: report.id,
        }] : [],
      });
      setShowViewModal(true);
    } catch {
      // best-effort — the already-analysed entry is already dismissed
    }
  }, [activeAnalysis, activeId, resetAnalysis]);

  const handleNewAnalysis = () => {
    // Show the form so the user can start another analysis.
    // The running analysis stays alive — its chip remains visible.
    setActiveId(null);
    setForm({ softwareName: "", policyUrl: "", notes: "" });
    setUploadedFile(null);
    setUrlError("");
  };

  const handleCancelAnalysis = () => {
    // User explicitly aborts the currently-viewed analysis.
    cancelAnalysis(activeId);
  };

  const [showHowTo, setShowHowTo] = useState(false);

  return (
    <div className="flex flex-col gap-4">
      {/* Page header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-brand-text">Analyze Privacy Policy</h1>
          <p className="text-sm text-brand-muted mt-1">
            Paste, type, or upload a privacy policy to begin AI-powered analysis.
          </p>
        </div>
        <button
          onClick={() => setShowHowTo(true)}
          className="flex-shrink-0 inline-flex items-center gap-2 px-4 py-2 rounded-xl border border-surface-border bg-white text-sm font-medium text-brand-muted hover:text-primary hover:border-primary transition-colors"
        >
          <HelpCircle className="w-4 h-4" />
          User Guide
        </button>
      </div>

      {/* How To Do modal */}
      {showHowTo && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div
            className="relative w-full bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden"
            style={{ maxWidth: 680, height: "82vh" }}
          >
            {/* Fixed header */}
            <div className="flex items-center justify-between px-6 py-3.5 border-b border-surface-border flex-shrink-0 bg-white">
              <div className="flex items-center gap-2.5">
                <div className="w-7 h-7 rounded-lg bg-[#ECE9FF] flex items-center justify-center">
                  <HelpCircle className="w-4 h-4 text-[#5B4FCF]" />
                </div>
                <p className="text-sm font-bold text-brand-text">How to Use AIPolicyScan</p>
              </div>
              <button
                onClick={() => setShowHowTo(false)}
                className="w-7 h-7 rounded-lg border border-surface-border flex items-center justify-center text-brand-muted hover:text-brand-text hover:bg-gray-100 transition-colors"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
            {/* Scrollable body — strip outer section padding */}
            <div className="overflow-y-auto flex-1 [&_section]:py-8 [&_section]:bg-white [&_.absolute.inset-0]:hidden">
              <HowToDoSection />
            </div>
          </div>
        </div>
      )}

      {/* Vendor Credits Exhausted banner */}
      {isCreditsExhausted && (
        <div className="rounded-2xl border border-orange-200 bg-orange-50 px-5 py-4 flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-orange-100 text-orange-500 flex items-center justify-center flex-shrink-0">
              <Wallet className="w-5 h-5" />
            </div>
            <div>
              <p className="text-sm font-semibold text-orange-500">Vendor Credits Exhausted</p>
              <p className="text-sm text-orange-400">
                You’ve used all {totalCredits} Vendor in your current plan. Upgrade to continue.
              </p>
              <div className="mt-1.5">
                <div className="w-48 h-2 bg-orange-200 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-orange-400 rounded-full transition-all"
                    style={{ width: `${Math.round((usedCredits / totalCredits) * 100)}%` }}
                  />
                </div>
                <p className="text-xs text-orange-400 mt-0.5">{remainingCredits} / {totalCredits} remaining</p>
              </div>
            </div>
          </div>
          <button className="px-5 py-2.5 rounded-xl bg-orange-500 text-white text-sm font-semibold hover:bg-orange-600 transition-colors whitespace-nowrap">
            Upgrade Plan →
          </button>
        </div>
      )}

      {/* ── Form view ── */}
      {view === "form" && (
        <>
          <div className="bg-white border border-surface-border rounded-2xl p-6">
            <form onSubmit={handleSubmit} className="space-y-5">

              {/* Software Name */}
              <div>
                <label className="block text-sm font-medium text-brand-text mb-1.5">
                  Software Name <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  name="softwareName"
                  value={form.softwareName}
                  onChange={handleChange}
                  placeholder="e.g. Google"
                  required
                  className="w-full px-4 py-2.5 border border-surface-border rounded-lg text-sm text-brand-text placeholder-brand-light bg-white focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition"
                />
              </div>

              {/* Privacy Policy URL */}
              <div>
                <label className="block text-sm font-medium text-brand-text mb-1.5">
                  Privacy Policy URL <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  name="policyUrl"
                  value={form.policyUrl}
                  onChange={handleChange}
                  placeholder="https://policies.google.com/privacy?hl=en-US"
                  required
                  className={`w-full px-4 py-2.5 border rounded-lg text-sm text-brand-text placeholder-brand-light bg-white focus:outline-none focus:ring-2 focus:border-primary transition ${
                    urlError
                      ? "border-danger focus:ring-danger/30"
                      : "border-surface-border focus:ring-primary/30"
                  }`}
                />
                {urlError && (
                  <p className="text-xs text-danger mt-1">{urlError}</p>
                )}
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-brand-text mb-1.5">
                  Notes
                </label>
                <textarea
                  name="notes"
                  value={form.notes}
                  onChange={handleChange}
                  placeholder="e.g. Q1 2026 compliance review"
                  rows={3}
                  className="w-full px-4 py-2.5 border border-surface-border rounded-lg text-sm text-brand-text placeholder-brand-light bg-white focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition resize-y"
                />
              </div>

              {/* Upload File */}
              <div>
                <label className="block text-sm font-medium text-brand-text mb-1.5">
                  Upload Policy File <span className="text-brand-light font-normal">(optional, max 5 MB — PDF, TXT, JSON, MD)</span>
                </label>
                <div className="flex items-center gap-2">
                  <div className="flex-1 px-4 py-2.5 border border-surface-border rounded-lg text-sm text-brand-light bg-white truncate">
                    {uploadedFile ? uploadedFile.name : "No file chosen"}
                  </div>
                  {uploadedFile && (
                    <button
                      type="button"
                      onClick={handleRemoveFile}
                      className="inline-flex items-center gap-2 border border-surface-border text-brand-muted px-4 py-2.5 rounded-lg text-sm font-semibold hover:bg-surface transition-colors whitespace-nowrap"
                    >
                      Remove
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="inline-flex items-center gap-2 bg-primary text-white px-4 py-2.5 rounded-lg text-sm font-semibold hover:bg-primary-dark transition-colors whitespace-nowrap"
                  >
                    <Upload className="w-4 h-4" />
                    Upload
                  </button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".pdf,.txt,.json,.md"
                    className="hidden"
                    onChange={handleFileChange}
                  />
                </div>
              </div>

              {/* Submit */}
              <button
                type="submit"
                disabled={
                  submitting ||
                  !form.policyUrl.trim() ||
                  !form.softwareName.trim() ||
                  isCreditsExhausted
                }
                className="flex items-center gap-2 bg-primary text-white px-6 py-2.5 rounded-lg text-sm font-semibold hover:bg-primary-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Analyzing…
                  </>
                ) : (
                  <>
                    <Zap className="w-4 h-4" />
                    Analyze Policy
                  </>
                )}
              </button>
            </form>
          </div>

          {/* Recent Analyses */}
          <div className="bg-white border border-surface-border rounded-2xl p-6">
            <h3 className="text-base font-semibold text-primary mb-4">Latest Analysis</h3>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-brand-muted text-xs font-medium border-b border-surface-border">
                  <th className="text-left pb-2 font-medium">Software Name</th>
                  <th className="text-left pb-2 font-medium">Privacy URL</th>
                  <th className="text-left pb-2 font-medium">Date & Time</th>
                  <th className="text-left pb-2 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {lastLoading ? (
                  <tr><td colSpan={4} className="py-6 text-center">
                    <Loader2 className="w-4 h-4 text-primary animate-spin inline-block" />
                  </td></tr>
                ) : lastAnalysis ? (
                  <tr className="border-b border-surface-border last:border-0">
                    <td className="py-3 font-semibold text-brand-text">{lastAnalysis.software_name}</td>
                    <td className="py-3 text-brand-muted text-xs break-all pr-3">{lastAnalysis.privacy_url || "—"}</td>
                    <td className="py-3 text-brand-muted">
                      {lastAnalysis.created_at
                        ? (() => {
                            const d = new Date(lastAnalysis.created_at);
                            const date = d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
                            const time = d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true });
                            return `${date} · ${time}`;
                          })()
                        : "—"}
                    </td>
                    <td className="py-3">
                      <button
                        onClick={handleViewLatest}
                        className="inline-flex items-center gap-1.5 text-primary text-sm hover:underline"
                      >
                        <Eye className="w-4 h-4" />
                        View
                      </button>
                    </td>
                  </tr>
                ) : (
                  <tr><td colSpan={4} className="py-6 text-center text-sm text-brand-muted">No analyses yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* ── Analyzing state ── */}
      {view === "analyzing" && (
        <div key={activeId} className="bg-white border border-surface-border rounded-2xl overflow-hidden">
          {/* Switching flash — plays on every activeId change because key forces remount */}
          <motion.div
            className="h-0.5 bg-gradient-to-r from-primary via-indigo-400 to-primary w-full"
            initial={{ scaleX: 0, opacity: 1 }}
            animate={{ scaleX: 1, opacity: 0 }}
            transition={{ duration: 0.55, ease: "easeOut" }}
            style={{ transformOrigin: "left" }}
          />
          <AnalyzingView
            step={step}
            softwareName={activeAnalysis?.softwareName}
            startTime={activeAnalysis?.startTime}
            onNewAnalysis={handleNewAnalysis}
            onCancelAnalysis={handleCancelAnalysis}
          />
        </div>
      )}

      {/* ── Handoff state: taking longer than the foreground window ── */}
      {view === "handoff" && (
        <motion.div
          key={`handoff-${activeId}`}
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, ease: "easeOut" }}
          className="bg-white border border-surface-border rounded-2xl overflow-hidden"
        >
          <div className="px-6 py-10 flex flex-col items-center text-center gap-5">
            <div className="relative w-16 h-16 flex items-center justify-center">
              <span className="absolute inset-0 rounded-full bg-primary/10 animate-ping" style={{ animationDuration: "2.4s" }} />
              <div className="relative w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
                <Clock className="w-7 h-7 text-primary" />
              </div>
            </div>

            <div className="max-w-md">
              <h2 className="text-xl font-bold text-brand-text">Your report is being generated</h2>
              <p className="text-sm text-brand-muted mt-2 leading-relaxed">
                {backgroundMsg || "This can take a few minutes. You can keep working — we'll notify you on the dashboard when it's ready."}
              </p>
              {activeAnalysis?.softwareName && (
                <p className="text-sm text-primary font-semibold mt-2">{activeAnalysis.softwareName}</p>
              )}
            </div>

            <div className="flex items-center gap-2 text-xs text-brand-muted bg-surface/60 border border-surface-border rounded-full px-3 py-1.5">
              <BellRing className="w-3.5 h-3.5 text-primary" />
              We'll post a notification on your dashboard when it's done.
            </div>

            <div className="flex items-center gap-3 flex-wrap justify-center pt-1">
              <button
                onClick={handleNewAnalysis}
                className="inline-flex items-center gap-2 bg-primary text-white px-5 py-2.5 rounded-xl text-sm font-semibold hover:bg-primary-dark transition-colors shadow-sm"
              >
                <Zap className="w-4 h-4" />
                Start another analysis
              </button>
              <button
                onClick={() => navigate("/history")}
                className="inline-flex items-center gap-2 text-primary border border-primary/30 bg-primary/5 px-5 py-2.5 rounded-xl text-sm font-semibold hover:bg-primary/10 transition-colors"
              >
                <Eye className="w-4 h-4" />
                View History
              </button>
            </div>
          </div>
        </motion.div>
      )}

      {/* ── Results Modal (after new analysis) ── */}
      {showModal && result && (
        <PolicyResultModal
          result={result}
          softwareName={result?.softwareName || activeAnalysis?.softwareName}
          onClose={() => resetAnalysis(activeId)}
          onNewAnalysis={handleNewAnalysis}
        />
      )}

      {/* ── View Latest Report Modal ── */}
      {showViewModal && viewRow && (
        <HistoryResultsModal
          row={viewRow}
          onClose={() => { setShowViewModal(false); setViewRow(null); }}
        />
      )}


      {/* ── Already Analyzed Confirmation ── */}
      {showAlreadyAnalyzed && (
        <AlreadyAnalyzedModal
          assetId={activeAnalysis?.existingAssetId}
          uploadedFile={uploadedFile}
          onCancel={() => resetAnalysis(activeId)}
          onView={handleViewExisting}
          onReanalyse={() => {
            // Capture before resetAnalysis clears activeAnalysis. Re-analyse runs
            // a genuinely new analysis (the /reanalyse endpoint re-runs the agent).
            const existingAssetId = activeAnalysis?.existingAssetId;
            const softwareName    = activeAnalysis?.softwareName ?? "";
            resetAnalysis(activeId); // remove the already-analysed entry, activeId→null
            if (existingAssetId) {
              runReanalysis({ existingAssetId, softwareName, transformFn: transformResult });
            }
          }}
          onReanalyseWithFile={() => {
            // Upload the selected file as a NEW policy version, then re-analyse.
            const existingAssetId = activeAnalysis?.existingAssetId;
            const softwareName    = activeAnalysis?.softwareName ?? "";
            const fileToSend      = uploadedFile;
            resetAnalysis(activeId);
            if (existingAssetId && fileToSend) {
              const fd = new FormData();
              fd.append("policyFile", fileToSend);
              runReanalysis({ existingAssetId, softwareName, transformFn: transformResult, formData: fd });
            }
          }}
        />
      )}

      {/* ── Plan Limit / Upgrade Prompt (402) ── */}
      {upgradeModal !== null && (
        <UpgradeLimitModal
          message={upgradeModal}
          onClose={() => resetAnalysis(activeId)}
        />
      )}

      {/* ── Free trial spent — collect a card (402 TRIAL_EXPIRED / TRIAL_RUNS_EXHAUSTED) ── */}
      {trialEndedMsg !== null && (
        <Modal
          isOpen
          onClose={() => resetAnalysis(activeId)}
          title="Add a card to keep analysing"
        >
          <p className="text-sm text-brand-muted mb-4">{trialEndedMsg}</p>
          <TrialPaymentStep
            planId={trialPlan?.id}
            userName={authState?.user?.name}
            userEmail={authState?.user?.email}
            planName={trialPlan?.name ?? 'Individual'}
            planPrice={trialPlan?.price ?? 0}
            onSuccess={() => { resetAnalysis(activeId); window.location.reload(); }}
            onBack={() => resetAnalysis(activeId)}
          />
        </Modal>
      )}

      {/* ── Error modal ── */}
      <AnimatePresence>
        {errorModalOpen && analyzeError && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-black/50"
              onClick={dismissError}
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.18 }}
              className="relative bg-white rounded-2xl shadow-xl w-full max-w-sm z-10 p-6 flex flex-col items-center gap-4"
            >
              <div className="w-12 h-12 rounded-full bg-red-50 flex items-center justify-center">
                <AlertCircle className="w-6 h-6 text-danger" />
              </div>
              <div className="text-center">
                <h3 className="text-base font-bold text-brand-text mb-1">{analyzeErrorTitle}</h3>
                <p className="text-sm text-brand-muted">{analyzeError}</p>
              </div>
              <button
                onClick={dismissError}
                className="w-full py-2.5 rounded-xl bg-primary text-white text-sm font-semibold hover:bg-primary-dark transition-colors"
              >
                OK
              </button>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AnalyzePage;
